import {
    CheckCircleIcon,
    DocumentIcon,
    InboxArrowDownIcon,
    TrashIcon,
} from "@heroicons/react/20/solid";
import { Link } from "@tanstack/react-router";
import { useCallback, useState } from "react";
import { AuthorName } from "components/common/AuthorChip";
import { Table as T, TableSortIcon } from "components/elements";
import { DiscussionsModal } from "components/elements/DiscussionsModal";
import { DeleteDocument } from "components/layout/documents/DeleteDocument";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useUserTimezone } from "hooks/useUserTimezone";
import { cn } from "lib/cn";
import { formatISODate } from "lib/formatISODate";
import { humanizeBytes } from "lib/humanizeBytes";
import { ClientDocument, ClientDocumentListEntry, DocumentsList } from "@compass/shared/types";

export type DocumentListProps = {
    documents: ClientDocumentListEntry[];
    onDownload?: (id: string, type?: string | null) => void;
    /** Controlled selection — managed by parent (e.g. ManageAttachmentsModal) */
    selectedIds?: Set<string>;
    onToggleSelect?: (document: ClientDocumentListEntry) => void;
    sort?: DocumentsList["sort"];
    sortBy?: DocumentsList["sortBy"];
    toggleSortField?: (field: DocumentsList["sortBy"]) => void;
};

export const DocumentsTable = (props: DocumentListProps) => {
    const user = useCurrentUser();
    const timeZone = useUserTimezone();
    const {
        documents,
        onDownload,
        selectedIds: controlledSelectedIds,
        onToggleSelect,
        sort,
        sortBy,
        toggleSortField,
    } = props;
    const [internalSelectedIds, setInternalSelectedIds] = useState<Set<string>>(new Set());
    const selectedIds = controlledSelectedIds ?? internalSelectedIds;
    const [showDocumentDiscussionsModal, setDocumentDiscussionsModal] = useState<boolean>(false);
    const [documentDiscussions, setDocumentDiscussions] = useState<ClientDocument | undefined>(
        undefined,
    );

    const Icon = useCallback(
        ({ field }: { field: DocumentsList["sortBy"] }) => {
            return null;
            // return <TableSortIcon<DocumentsList> sort={sort} sortBy={sortBy} field={field} />;
        },
        [sort, sortBy],
    );

    if (documents.length === 0) {
        return null;
    }

    return (
        <>
            <T.Table className={`h-96 w-fit border-0 shadow-none`}>
                <T.Head>
                    <T.Row className="border-0 ">
                        <T.IconCell
                            className="__cursor-pointer group border-0 bg-[#2DA7E0] text-white"
                            icon={<Icon field="name" />}
                            onClick={() => toggleSortField?.("name")}
                        >
                            Name
                        </T.IconCell>
                        <T.IconCell
                            className="__cursor-pointer group border-0 bg-[#2DA7E0] text-white"
                            icon={<Icon field="uploaded" />}
                            onClick={() => toggleSortField?.("uploaded")}
                        >
                            Uploaded
                        </T.IconCell>
                        <T.Cell className="border-0 bg-[#2DA7E0] text-white">References</T.Cell>
                        <T.Cell className="border-0 bg-[#2DA7E0] text-white">Visibility</T.Cell>
                        <T.IconCell
                            className="__cursor-pointer group bg-[#2DA7E0] text-white"
                            icon={<Icon field="owner" />}
                            onClick={() => toggleSortField?.("owner")}
                        >
                            Owner
                        </T.IconCell>
                        <T.Cell className="border-0 bg-[#2DA7E0] text-white">Organizations</T.Cell>
                        {onDownload && (
                            <T.Cell className="border-0 bg-[#2DA7E0] text-white">
                                <div className="text-right">Actions</div>
                            </T.Cell>
                        )}
                    </T.Row>
                </T.Head>
                <T.Body>
                    {documents.map((document) => (
                        <T.Row
                            key={document.id}
                            onClick={() => {
                                if (onToggleSelect) {
                                    onToggleSelect(document);
                                } else {
                                    const updated = new Set(internalSelectedIds);
                                    if (updated.has(document.id)) {
                                        updated.delete(document.id);
                                    } else {
                                        updated.add(document.id);
                                    }
                                    setInternalSelectedIds(updated);
                                }
                            }}
                            className={cn(
                                "cursor-pointer transition-colors",
                                selectedIds.has(document.id)
                                    ? "bg-blue-100 ring-2 ring-inset ring-blue-400"
                                    : "even:bg-brand-polarice odd:bg-[#edf2f7] hover:bg-blue-50",
                            )}
                        >
                            <T.Cell>
                                <div className="flex items-center">
                                    {selectedIds.has(document.id) ? (
                                        <CheckCircleIcon className="h-6 w-6 shrink-0 p-0.5 text-blue-600" />
                                    ) : (
                                        <DocumentIcon className="h-6 w-6 p-1 text-[#424242]" />
                                    )}
                                    <div className="flex flex-row items-baseline gap-2">
                                        <div
                                            className={cn(
                                                "font-medium text-slate-700",
                                                onDownload && "cursor-pointer hover:underline",
                                            )}
                                            onClick={() => onDownload?.(document.id)}
                                        >
                                            {document.displayName || document.name}
                                        </div>
                                        {document.sizeBytes && (
                                            <div className="text-sm text-[#424242]">
                                                {humanizeBytes(document.sizeBytes)}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </T.Cell>
                            <T.Cell>
                                <div className="text-[#424242]">
                                    {formatISODate(document.uploaded, timeZone)}
                                </div>
                            </T.Cell>
                            <T.Cell>
                                <div
                                    onClick={() => {
                                        if (document?.references) {
                                            setDocumentDiscussionsModal(Boolean(document));
                                            setDocumentDiscussions(document);
                                        }
                                    }}
                                    className="cursor-pointer px-3 text-[#424242]"
                                >
                                    {document.references}
                                </div>
                            </T.Cell>
                            <T.Cell>
                                <span
                                    className={cn(
                                        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                                        document.context === "PUBLIC_ATTACHMENT"
                                            ? "bg-green-100 text-green-700"
                                            : "bg-gray-100 text-gray-600",
                                    )}
                                >
                                    {document.context === "PUBLIC_ATTACHMENT"
                                        ? "Public"
                                        : "Private"}
                                </span>
                            </T.Cell>
                            <T.Cell>
                                <div className="text-[#424242]">
                                    <AuthorName
                                        author={document.author}
                                        surface="private"
                                        profileRoute="/social/profile/$id"
                                        target="_blank"
                                    />
                                </div>
                            </T.Cell>
                            <T.Cell>
                                <div className="text-[#424242]">
                                    {document.organizations
                                        ? document.organizations.join(", ")
                                        : " - "}
                                </div>
                            </T.Cell>

                            {onDownload && (
                                <T.Cell>
                                    <div className="flex flex-row items-center gap-1">
                                        <InboxArrowDownIcon
                                            className="ml-auto h-6 w-6 cursor-pointer p-1 text-[#424242]"
                                            onClick={() => onDownload(document.id, document.type)}
                                        />
                                        {document.userCanEdit && (
                                            <DeleteDocument document={document} />
                                        )}
                                    </div>
                                </T.Cell>
                            )}
                        </T.Row>
                    ))}
                </T.Body>
            </T.Table>

            {showDocumentDiscussionsModal && (
                <DiscussionsModal
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    document={documentDiscussions}
                    isOpen={showDocumentDiscussionsModal}
                    setIsOpen={setDocumentDiscussionsModal}
                />
            )}
        </>
    );
};
